import server from '../serverUrl';

export const getApiErrorMessage = (error, fallbackMessage = 'Something went wrong') => {
  if (!error) return fallbackMessage;

  if (typeof error === 'string') {
    return error.replace(/^Error:\s*/i, '').trim() || fallbackMessage;
  }

  const responseData = error?.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData.trim();
  }

  const message =
    responseData?.message ||
    responseData?.error ||
    responseData?.details ||
    error?.message;

  return message || fallbackMessage;
};

export const isApiErrorResponse = (response) => {
  if (!response) return true;

  if (typeof response === 'string') {
    return /error|network|failed|cors/i.test(response);
  }

  return Boolean(response?.error) || response?.success === false;
};

export const isImageFileUrl = (url = '') => /\.(png|jpe?g|gif|bmp|webp|svg|avif)(?:[?#].*)?$/i.test(url);

export const isPdfFileUrl = (url = '') => /\.pdf(?:[?#].*)?$/i.test(url);

export const isOfficeFileUrl = (url = '') => /\.(docx?|xlsx?|pptx?|csv|txt|rtf)(?:[?#].*)?$/i.test(url);

export const MAX_UPLOAD_SIZE_MB = 10;
export const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

export const getUploadSizeErrorMessage = (fileName = 'File') =>
  `${fileName} exceeds the maximum upload size of ${MAX_UPLOAD_SIZE_MB}MB.`;

// File size limits for preview services (in bytes)
export const PREVIEW_SIZE_LIMITS = {
  PDF: MAX_UPLOAD_SIZE_BYTES,
  OFFICE: MAX_UPLOAD_SIZE_BYTES,
  IMAGE: MAX_UPLOAD_SIZE_BYTES,
};

// Check if file size is too large for preview
export const isFileTooLargeForPreview = (url = '', fileSize = null) => {
  if (!url) return false;

  // If we have fileSize, use it directly
  if (fileSize !== null) {
    if (isPdfFileUrl(url)) return fileSize > PREVIEW_SIZE_LIMITS.PDF;
    if (isOfficeFileUrl(url)) return fileSize > PREVIEW_SIZE_LIMITS.OFFICE;
    if (isImageFileUrl(url)) return fileSize > PREVIEW_SIZE_LIMITS.IMAGE;
  }

  // Fallback: check URL parameters or filename for size hints
  const urlObj = new URL(url);
  const sizeParam = urlObj.searchParams.get('size') || urlObj.searchParams.get('fileSize');
  if (sizeParam) {
    const size = parseInt(sizeParam, 10);
    if (!isNaN(size)) {
      if (isPdfFileUrl(url)) return size > PREVIEW_SIZE_LIMITS.PDF;
      if (isOfficeFileUrl(url)) return size > PREVIEW_SIZE_LIMITS.OFFICE;
      if (isImageFileUrl(url)) return size > PREVIEW_SIZE_LIMITS.IMAGE;
    }
  }

  return false;
};

export const getBackendServerUrl = () => {
  if (typeof server === 'string') return server;
  if (server && server.server) return server.server;
  return 'https://api.docity.co';
};

export const normalizeFileUrl = (url = '') => {
  if (!url) return '';
  if (typeof url !== 'string') return '';

  let trimmed = url.trim();

  // If URL is wrapped in backend proxy (e.g. /proxy-pdf?url=...), unwrap it to get the direct file URL
  if (trimmed.includes('/proxy-pdf?url=')) {
    const parts = trimmed.split('/proxy-pdf?url=');
    if (parts[1]) {
      try {
        trimmed = decodeURIComponent(parts[1]).trim();
      } catch (e) {
        trimmed = parts[1].trim();
      }
    }
  }

  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
    return trimmed;
  }

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Handle relative S3 paths like "userDocuments/users/..."
  const cleanPath = trimmed.replace(/^\/+/, '');
  return `https://docitydocuments.s3.amazonaws.com/${cleanPath}`;
};

export const getProxyUrl = (url = '') => {
  if (!url) return '';
  return normalizeFileUrl(url);
};

export const getDocumentPreviewUrl = (url = '') => {
  if (!url) return '';
  const normalized = normalizeFileUrl(url);

  if (isOfficeFileUrl(normalized)) {
    const isLocalOrBlob = normalized.startsWith('blob:') || normalized.startsWith('data:') || normalized.includes('localhost') || normalized.includes('127.0.0.1');
    if (isLocalOrBlob) return normalized;
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(normalized)}`;
  }

  return normalized;
};